import { Box, styled, Switch } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';

const StyledDataGridPro = styled(DataGridPro)(({ theme }) => ({
  // MUI X v8 sets `z-index: 0` on `.MuiDataGrid-virtualScroller` to isolate
  // its stacking context (mui/mui-x#10547). With detail panels enabled in
  // Athena, that context caused `.MuiDataGrid-virtualScrollerContent` to
  // paint over the absolutely-positioned RenderZone, hiding all rows.
  // Remove the context and lift RenderZone above its sibling, but stay
  // under the sticky column headers (z:40).
  '& .MuiDataGrid-virtualScroller': {
    zIndex: 'auto',
  },
  '& .MuiDataGrid-virtualScrollerRenderZone': {
    zIndex: 10,
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: theme.palette.primary.light,
    cursor: 'pointer',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  // Remove focus outlines without adding background color (prevents multiple highlighted rows)
  '& .MuiDataGrid-row:focus, & .MuiDataGrid-row:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  // Highlight rows with expanded detail panels
  '& .MuiDataGrid-row--detailPanelExpanded': {
    backgroundColor: theme.palette.action.selected,
    '& .MuiDataGrid-cell': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  // Highlight the selected row in Tyto Deep Blue (#1F4D83) — shared by the
  // Projects table and the admin Users table.
  '& .selected-project-row': {
    backgroundColor: '#1F4D83',
    '& .MuiDataGrid-cell': {
      backgroundColor: '#1F4D83',
      color: '#FFFFFF',
    },
    '&:hover': {
      backgroundColor: '#163C66',
      '& .MuiDataGrid-cell': {
        backgroundColor: '#163C66',
      },
    },
  },
  // Remove borders/outlines from detail panel toggle icons
  '& .MuiDataGrid-detailPanelToggleCell': {
    '& .MuiIconButton-root': {
      border: 'none',
      outline: 'none',
      '&:focus': {
        border: 'none',
        outline: 'none',
      },
      '&:active': {
        border: 'none',
        outline: 'none',
      },
      '&:focus-visible': {
        border: 'none',
        outline: 'none',
      },
    },
  },
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .MuiDataGrid-row:hover': {
    backgroundColor: theme.palette.primary.light,
    cursor: 'pointer',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  // Remove focus outlines without adding background color (prevents multiple highlighted rows)
  '& .MuiDataGrid-row:focus, & .MuiDataGrid-row:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  // Highlight rows with expanded detail panels
  '& .MuiDataGrid-row--detailPanelExpanded': {
    backgroundColor: theme.palette.action.selected,
    '& .MuiDataGrid-cell': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  // Tyto Deep Blue selection — matches the Projects + admin Users tables on
  // StyledDataGridPro so selection feels consistent across all grids.
  '& .selected-project-row': {
    backgroundColor: '#1F4D83',
    '& .MuiDataGrid-cell': {
      backgroundColor: '#1F4D83',
      color: '#FFFFFF',
    },
    '&:hover': {
      backgroundColor: '#163C66',
      '& .MuiDataGrid-cell': {
        backgroundColor: '#163C66',
      },
    },
  },
}));

const HeaderContainer = styled(Box, {
  name: 'view-header-container',
  slot: 'Root',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1),
  // borderBottom: `1px solid ${theme.palette.primary.main}`,
}));

const AthenaSwitch = styled(Switch)(({ theme }) => ({
  width: 54,
  height: 28,
  padding: 7,
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(22px)',
      '& .MuiSwitch-thumb:before': {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
          theme.palette.getContrastText(theme.palette.primary.main),
        )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      },
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#aab4be',
        ...theme.applyStyles('dark', {
          backgroundColor: '#8796A5',
        }),
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: '#1c8cfd',
    width: 24,
    height: 24,
    '&::before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
    ...theme.applyStyles('dark', {
      backgroundColor: '#1c8cfd',
    }),
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: '#aab4be',
    borderRadius: 20 / 2,
    ...theme.applyStyles('dark', {
      backgroundColor: '#8796A5',
    }),
  },
}));

export { AthenaSwitch, HeaderContainer, StyledDataGrid, StyledDataGridPro };
